import './components/WebChat.css';
import './components/GroupMgmt.css';
import './components/Dialog.css';
import './components/Org.css';
import './components/shellTheme.css';

import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { SearchBox } from '@fluentui/react/lib/SearchBox';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import { I18nextProvider, useTranslation } from 'react-i18next';
import chatI18n from './i18n';
import { chatSocket } from './dataProvider/socket';
import { WebChat } from './components/WebChat';
import { ChatRoomHeader } from './components/ChatRoomHeader';
import { PromptPickShareTarget } from './components/PromptPickShareTarget';
import { PromptContactList } from './components/PromptContactList';
import { GroupListHeader } from './components/GroupListHeader';
import GroupListItem from './components/GroupMgmt/GroupListItem';
import ConfirmDialog from './components/common/ConfirmDialog';
import { useUserGroups } from './hooks/useUserGroups';
import { _dataProvider, type IUser } from './dataProvider/DataProvider';
import { getJwtObj, _smallScreen } from './SharedCommon/utils';
import { registerLaunchOneOnOneHandler, getIdigestChatHostConfig } from './chatHost';
import { openMeeting } from './SharedCommon/Meeting';
import {
  groupDisplayName,
  getPendingReadStorageKey,
  loadPendingReadGroupIds,
  savePendingReadGroupIds,
  normalizeTopGroupIds,
  isTopGroupId,
  sortGroupsWithTopFirst,
  toggleTopGroupIds
} from './components/groupUtils';
import type { IContactItem, IGroupItemView } from './components/types';
import ConferenceIcon from './img/icon-Conference-white.png';
import ConferenceHoverIcon from './img/icon-Conference-mouseover.png';
import ManageMembersIcon from './img/icon-Manage-Members-white.png';
import ManageMembersHoverIcon from './img/icon-Goup-mouseover.png';
import { getSearchBoxStyles } from './components/shellStyles';
import { SearchBoxChrome } from './components/SearchBoxChrome';
import { chatThemeCssVars, getChatThemeTokens, type IdigestChatTheme } from './theme';
import { IdigestChatThemeProvider } from './ThemeContext';
import OneOnOneGreetingDialog from './components/OneOnOneGreetingDialog';

// Fluent Checkbox / Icon glyphs (checkmark) need MDL2 fonts. WebIDigestApp
// already calls this elsewhere; vine-web does not — init here for both hosts.
initializeIcons();

const AddNewGroupWindow = lazy(() => import('./components/AddNewGroupWindow'));
const AddMassMessagesWindow = lazy(() => import('./components/AddMassMessagesWindow'));
const GroupMemberTool = lazy(() => import('./components/GroupMemberTool'));

function formatDisappearingMessagesTime(
  minutes: number,
  t: (key: string, options?: Record<string, unknown>) => string
) {
  if (minutes % (7 * 24 * 60) === 0) {
    return t('GroupScreen.AutoDeleteNumWeeks', { value: minutes / (7 * 24 * 60) });
  }
  if (minutes % (24 * 60) === 0) {
    return t('GroupScreen.AutoDeleteNumDays', { value: minutes / (24 * 60) });
  }
  if (minutes % 60 === 0) {
    return t('GroupScreen.AutoDeleteNumHours', { value: minutes / 60 });
  }
  return t('GroupScreen.AutoDeleteNumMins', { value: minutes });
}

/** stack: list ↔ detail (vine-web). split: list | detail side-by-side (WebIDigestApp desktop). */
export type IdigestChatLayout = 'stack' | 'split';

export interface IdigestChatShellProps {
  height: string;
  className?: string;
  /** Host color scheme. Defaults to light (WebIDigestApp). Vine should pass resolved theme. */
  theme?: IdigestChatTheme;
  /**
   * Open (or create) the one-on-one conversation for this iDigest user after
   * the group list has loaded. Existing conversations keep their original
   * group id, so WebChat loads the full message history.
   */
  initialOneOnOneUserId?: number;
  /** Login email or seven-digit iDigest id used only for the stranger greeting flow. */
  initialOneOnOneLoginId?: string;
  /** Display name shown in the stranger greeting dialog. */
  initialOneOnOneDisplayName?: string;
  /** When set, create a missing 1:1 chat with this greeting without showing the greeting dialog. */
  initialOneOnOneAutomaticGreeting?: string;
  /** Reports whether an initial target is resolving, greeting a stranger, or showing chat. */
  onInitialOneOnOneViewChange?: (view: 'resolving' | 'greeting' | 'chat') => void;
  /** Called when the initial stranger greeting dialog is dismissed. */
  onInitialOneOnOneDismiss?: () => void;
  /** Called after a stranger greeting creates the conversation and sends successfully. */
  onInitialOneOnOneGreetingComplete?: () => void;
  /** Open this group after the group list has loaded (e.g. right after joining via an invite link). */
  initialGroupId?: number;
  /**
   * Navigation layout.
   * - `stack` (default): full-width list; open a group to replace with chat (Back returns to list).
   * - `split`: desktop keeps list on the left and chat in the middle (legacy WebIDigestApp).
   *   On small screens, split falls back to stack behavior.
   */
  layout?: IdigestChatLayout;

  discussionId?: number;
  /**
   * Navigation used while viewing a shared-answer discussion.
   * - `list` (default): preserve the existing side/stack group list.
   * - `tabs`: keep the chat visible and show Public/groups across the top.
   */
  discussionNavigation?: 'list' | 'tabs';
}

type BatchHandlers = {
  onExit: () => void;
  onGroupCollection: () => void;
  onMyCollection: () => void;
  onDelete: () => void;
  onRemoveGroupCollection?: () => void;
  onRemoveMyCollection?: () => void;
};

const circularButtonStyles = {
  root: {
    width: '30px',
    height: '30px',
    minWidth: '30px',
    minHeight: '30px',
    padding: 0,
    margin: 0,
    borderRadius: '50%',
    backgroundColor: 'transparent',
    fontSize: '16px',
    fontWeight: '600',
    position: 'relative' as const,
    selectors: {
      '&.ms-Button': {
        borderRadius: '50%'
      }
    }
  },
  rootHovered: {
    backgroundColor: 'transparent'
  },
  rootPressed: {
    backgroundColor: 'transparent'
  },
  flexContainer: {
    justifyContent: 'center' as const,
    alignItems: 'center' as const
  }
};

function buildCurrentUserFromJwt(): IUser {
  const jwt = getJwtObj() as {
    userId?: number;
    loginId?: string;
    uniqueId?: string;
    displayName?: string;
  };
  return {
    userId: jwt.userId ?? -1,
    loginId: jwt.loginId || '',
    uniqueId: jwt.uniqueId || jwt.loginId || '',
    displayName: jwt.displayName || '',
    isGroupLeader: false
  };
}

export function IdigestChatShell(props: IdigestChatShellProps) {
  useEffect(() => chatSocket.retain(), []);
  // Isolated chat i18n — must wrap before any useTranslation in this tree so we
  // never bind vine-web's react-i18next singleton to chat resources.
  return (
    <I18nextProvider i18n={chatI18n}>
      <IdigestChatShellInner {...props} />
    </I18nextProvider>
  );
}

function IdigestChatShellInner({
  height,
  className,
  theme = 'light',
  layout = 'stack',
  discussionId,
  discussionNavigation = 'list',
  initialOneOnOneUserId,
  initialOneOnOneLoginId,
  initialOneOnOneDisplayName,
  initialOneOnOneAutomaticGreeting,
  onInitialOneOnOneViewChange,
  onInitialOneOnOneDismiss,
  onInitialOneOnOneGreetingComplete,
  initialGroupId
}: IdigestChatShellProps) {
  const { t } = useTranslation();
  const { groupList, txt, loading, searchQuery, setSearchQuery, reloadGroups, updateGroupInList } = useUserGroups();
  const useDiscussionTabs = !!discussionId && discussionNavigation === 'tabs';
  // Desktop split keeps list + detail; mobile always uses stack navigation.
  // Discussion tabs are already a complete full-width layout, so they override split.
  const useSplitLayout = layout === 'split' && !_smallScreen && !useDiscussionTabs;

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [discussionTags, setDiscussionTags] = useState<number[]>([]);
  const [selectedDiscussionTag, setSelectedDiscussionTag] = useState<number | null>(null);

  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set());
  const [groupSelectionMode, setGroupSelectionMode] = useState(false);
  const [leaveGroup, setLeaveGroup] = useState<IGroupItemView | null>(null);
  const [isLeavingGroup, setIsLeavingGroup] = useState(false);
  const [visitedGroups, setVisitedGroups] = useState<number[]>([]);
  const [contactList, setContactList] = useState<IContactItem[]>([]);
  const [currentUser, setCurrentUser] = useState<IUser>(buildCurrentUserFromJwt);
  const [userHideMessages, setUserHideMessages] = useState(0);

  const [showContacts, setShowContacts] = useState(false);
  const [showAddNewGroup, setShowAddNewGroup] = useState(false);
  const [showMassMessage, setShowMassMessage] = useState(false);
  const [showMemberManage, setShowMemberManage] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteFailedDialog, setShowDeleteFailedDialog] = useState(false);
  const [deleteErrorInfo, setDeleteErrorInfo] = useState<string>();
  const [isDeleting, setIsDeleting] = useState(false);
  const initialOneOnOneRequestRef = useRef<number | null>(null);
  const initialGroupRequestRef = useRef<number | null>(null);

  const [isBatchSelectMode, setIsBatchSelectMode] = useState(false);
  const [selectedMessageCount, setSelectedMessageCount] = useState(0);
  const [hasOthersMessages, setHasOthersMessages] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'none' | 'user' | 'group' | 'file'>('none');
  const webChatBatchHandlersRef = useRef<BatchHandlers | null>(null);

  const [shareMessages, setShareMessages] = useState<any[]>([]);
  const [shareSourceGroupId, setShareSourceGroupId] = useState(-1);
  const [shareMode, setShareMode] = useState<'individual' | 'merged'>('individual');
  const [showShareTargetDialog, setShowShareTargetDialog] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ show: false, content: '' });
  const [showInitialGreeting, setShowInitialGreeting] = useState(false);

  const [isConferenceHovered, setIsConferenceHovered] = useState(false);
  const [isManageHovered, setIsManageHovered] = useState(false);

  const jwtUserId = (getJwtObj() as { userId?: number }).userId;
  const pendingReadStorageKey =
    jwtUserId === undefined || jwtUserId === null || String(jwtUserId) === '-1'
      ? null
      : getPendingReadStorageKey(jwtUserId);
  const [pendingReadGroupIds, setPendingReadGroupIds] = useState<Set<number>>(() =>
    loadPendingReadGroupIds(pendingReadStorageKey)
  );
  const [entryUnreadGroupIds, setEntryUnreadGroupIds] = useState<ReadonlySet<number> | null>(null);
  const [topGroupIds, setTopGroupIds] = useState<number[]>([]);
  const topGroupIdSet = useMemo(() => new Set(topGroupIds), [topGroupIds]);

  const tokens = useMemo(() => getChatThemeTokens(theme), [theme]);
  const searchBoxStyles = useMemo(() => getSearchBoxStyles(theme), [theme]);

  const rootClassName = [
    'idigest-chat',
    theme === 'dark' ? 'idigest-chat--dark' : 'idigest-chat--light',
    useSplitLayout ? 'idigest-chat--split' : null,
    useDiscussionTabs ? 'idigest-chat--discussion-tabs' : null,
    className
  ]
    .filter(Boolean)
    .join(' ');

  const rootStyle: React.CSSProperties = {
    height,
    flex: height === '100%' || String(height).includes('%') ? 1 : undefined,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: tokens.bg,
    color: tokens.fg,
    ...chatThemeCssVars(tokens)
  };

  const showAlertDialog = useCallback((message: string) => {
    setAlertDialog({ show: true, content: message });
  }, []);

  const updatePendingReadGroupIds = useCallback(
    (updater: (ids: Set<number>) => Set<number>) => {
      if (!pendingReadStorageKey) {
        return;
      }
      setPendingReadGroupIds((prev) => {
        const next = updater(prev);
        savePendingReadGroupIds(pendingReadStorageKey, next);
        return next;
      });
    },
    [pendingReadStorageKey]
  );

  useEffect(() => {
    setPendingReadGroupIds(loadPendingReadGroupIds(pendingReadStorageKey));
  }, [pendingReadStorageKey]);

  // Capture red-dot priority once per entry. Reading a group clears its dot without moving its row.
  useEffect(() => {
    if (loading || entryUnreadGroupIds !== null) {
      return;
    }
    setEntryUnreadGroupIds(
      new Set(
        groupList
          .filter((group) => group.newChatMessage || pendingReadGroupIds.has(group.groupId))
          .map((group) => group.groupId)
      )
    );
  }, [loading, groupList, pendingReadGroupIds, entryUnreadGroupIds]);

  useEffect(() => {
    setDiscussionTags([]);
    setSelectedDiscussionTag(null);
  }, [discussionId, useDiscussionTabs]);

  useEffect(() => {
    let cancelled = false;
    void _dataProvider.getGroupViewTopList().then((ids) => {
      if (!cancelled) {
        setTopGroupIds(normalizeTopGroupIds(ids));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [jwtUserId]);

  // Mobile browsers keep backgrounded tabs alive for hours; refresh last-message times when the user returns.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !loading) {
        void reloadGroups({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [loading, reloadGroups]);

  useEffect(() => {
    _dataProvider.getMyProfile(true).then((profile) => {
      if (!profile) {
        return;
      }
      setUserHideMessages(Number((profile as { hideMessages?: number }).hideMessages) || 0);
      setCurrentUser((prev) => ({
        ...prev,
        ...profile,
        uniqueId: (profile as IUser).uniqueId || (profile as IUser).loginId || prev.uniqueId || ''
      }));
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    _dataProvider.getUserContacts().then((contacts) => {
      if (cancelled) {
        return;
      }
      const list = Array.isArray(contacts) ? contacts : [];
      setContactList(
        [...list].sort((a: IContactItem, b: IContactItem) =>
          (a.displayName || a.name || '').localeCompare(b.displayName || b.name || '')
        )
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const launchOneOnOneWithFriend = useCallback(
    async (friendId: number, introduction: string, loginId: string) => {
      const existing = groupList.find(
        (group) => group.isOneOnOneGroup && String(group.friendUserId) === String(friendId)
      );

      if (existing) {
        if (introduction) {
          const sendResult = await _dataProvider.postGroupMessage(existing.groupId, introduction);
          if (sendResult === 'FAILED') {
            return false;
          }
        }
        setSelectedGroupId(existing.groupId);
        return true;
      }

      // The iDigest API accepts either a numeric user id or a unique/login id.
      // Vine only needs to expose the public numeric user id on post payloads.
      const targetId = loginId || String(friendId);
      const newGroup = await _dataProvider.createOneOnOneGroup(targetId);
      if (!newGroup) {
        return false;
      }

      if (introduction) {
        const sendResult = await _dataProvider.postGroupMessage(newGroup.studyGroupId, introduction);
        if (sendResult === 'FAILED') {
          return false;
        }
      }

      setContactList((prev) => {
        if (prev.some((c) => c.loginId === targetId)) {
          return prev;
        }
        return [
          {
            name: `${newGroup.studyGroupName} (${targetId})`,
            displayName: newGroup.studyGroupName,
            userId: friendId,
            loginId: targetId
          },
          ...prev
        ];
      });

      await reloadGroups();
      setSelectedGroupId(newGroup.studyGroupId);
      return true;
    },
    [groupList, reloadGroups]
  );

  useEffect(() => {
    if (initialOneOnOneUserId == null || initialOneOnOneUserId <= 0) {
      return;
    }
    if (initialOneOnOneRequestRef.current === initialOneOnOneUserId) {
      return;
    }

    onInitialOneOnOneViewChange?.('resolving');
    if (loading) {
      return;
    }

    initialOneOnOneRequestRef.current = initialOneOnOneUserId;

    const existing = groupList.find(
      (group) => group.isOneOnOneGroup && String(group.friendUserId) === String(initialOneOnOneUserId)
    );
    if (existing) {
      setSelectedGroupId(existing.groupId);
      onInitialOneOnOneViewChange?.('chat');
      return;
    }

    if (initialOneOnOneLoginId?.trim()) {
      if (initialOneOnOneAutomaticGreeting) {
        void (async () => {
          const result = await _dataProvider.inviteUser(initialOneOnOneLoginId, initialOneOnOneAutomaticGreeting);
          if (!result || result.emailSent) {
            showAlertDialog(t('FailedToAddContact'));
            initialOneOnOneRequestRef.current = null;
            return;
          }
          const opened = await launchOneOnOneWithFriend(
            initialOneOnOneUserId,
            initialOneOnOneAutomaticGreeting,
            String(result.userId || initialOneOnOneLoginId)
          );
          if (opened) {
            onInitialOneOnOneViewChange?.('chat');
          } else {
            showAlertDialog(t('FailedToAddContact'));
            initialOneOnOneRequestRef.current = null;
          }
        })();
        return;
      }
      setShowInitialGreeting(true);
      onInitialOneOnOneViewChange?.('greeting');
      return;
    }

    void launchOneOnOneWithFriend(initialOneOnOneUserId, '', String(initialOneOnOneUserId)).then((opened) => {
      if (opened) {
        onInitialOneOnOneViewChange?.('chat');
      } else if (initialOneOnOneRequestRef.current === initialOneOnOneUserId) {
        initialOneOnOneRequestRef.current = null;
      }
    });
  }, [
    groupList,
    initialOneOnOneLoginId,
    initialOneOnOneAutomaticGreeting,
    initialOneOnOneUserId,
    launchOneOnOneWithFriend,
    loading,
    onInitialOneOnOneViewChange,
    showAlertDialog,
    t
  ]);

  useEffect(() => {
    if (initialGroupId == null || initialGroupId <= 0 || loading) {
      return;
    }
    if (initialGroupRequestRef.current === initialGroupId) {
      return;
    }
    if (groupList.some((group) => Number(group.groupId) === initialGroupId)) {
      initialGroupRequestRef.current = initialGroupId;
      setSelectedGroupId(initialGroupId);
    }
  }, [groupList, initialGroupId, loading]);

  const handleInitialGreetingSubmit = useCallback(
    async (greeting: string) => {
      if (!initialOneOnOneLoginId || !initialOneOnOneUserId) {
        return false;
      }

      const result = await _dataProvider.inviteUser(initialOneOnOneLoginId, greeting);
      if (!result || result.emailSent) {
        showAlertDialog(t('FailedToAddContact'));
        return false;
      }

      const opened = await launchOneOnOneWithFriend(
        initialOneOnOneUserId,
        greeting,
        String(result.userId || initialOneOnOneLoginId)
      );
      if (!opened) {
        showAlertDialog(t('FailedToAddContact'));
        return false;
      }

      setShowInitialGreeting(false);
      if (onInitialOneOnOneGreetingComplete) {
        onInitialOneOnOneGreetingComplete();
      } else {
        onInitialOneOnOneViewChange?.('chat');
      }
      return true;
    },
    [
      initialOneOnOneLoginId,
      initialOneOnOneUserId,
      launchOneOnOneWithFriend,
      onInitialOneOnOneGreetingComplete,
      onInitialOneOnOneViewChange,
      showAlertDialog,
      t
    ]
  );

  useEffect(() => {
    registerLaunchOneOnOneHandler(launchOneOnOneWithFriend);
    return () => {
      registerLaunchOneOnOneHandler(() => undefined);
    };
  }, [launchOneOnOneWithFriend]);

  useEffect(() => {
    setIsBatchSelectMode(false);
    setSelectedMessageCount(0);
    setHasOthersMessages(false);
    setActiveFilter('none');
    setIsConferenceHovered(false);
    setIsManageHovered(false);
  }, [selectedGroupId]);

  const selectedGroup = useMemo(() => {
    if (selectedGroupId === null) {
      return undefined;
    }
    return groupList.find((group) => group.groupId === selectedGroupId) as IGroupItemView | undefined;
  }, [groupList, selectedGroupId]);

  const selectedGroupName = selectedGroup ? groupDisplayName(selectedGroup) : '';
  const selectedGroupDisappearingMessages = selectedGroup?.disappearingMessages;
  const selectedGroupAutoDeleteNotice =
    typeof selectedGroupDisappearingMessages === 'number' && selectedGroupDisappearingMessages > 0
      ? t('GroupScreen.MsgAutoDelete', {
          time: formatDisappearingMessagesTime(selectedGroupDisappearingMessages, t)
        })
      : undefined;
  const currentUserIsLeader = !!selectedGroup?.isGroupLeader;
  const yesGroupManagement = !!(selectedGroup && !selectedGroup.isOrgGroup && !selectedGroup.isOneOnOneGroup);

  const handleGroupInfoUpdated = useCallback(
    (groupInfo: any) => {
      const updatedGroupId = Number(groupInfo?.groupId ?? groupInfo?.GroupId ?? groupInfo?.id);
      if (!Number.isFinite(updatedGroupId)) {
        return;
      }

      const normalizedDisappearingMessages = Number(groupInfo?.disappearingMessages) || 0;
      const normalizedName = groupInfo?.name || groupInfo?.Name;

      updateGroupInList(updatedGroupId, {
        ...(normalizedName ? { name: normalizedName } : {}),
        disappearingMessages: normalizedDisappearingMessages
      });
    },
    [updateGroupInList]
  );

  const hiddenGroupsKey = `idigest-chat:hidden-groups:${jwtUserId}`;
  const [hiddenGroupIds, setHiddenGroupIds] = useState<Set<number>>(new Set());
  useEffect(() => {
    try {
      const ids: unknown = JSON.parse(localStorage.getItem(hiddenGroupsKey) || '[]');
      setHiddenGroupIds(new Set(Array.isArray(ids) ? ids.filter((id) => typeof id === 'number') : []));
    } catch {
      setHiddenGroupIds(new Set());
    }
  }, [hiddenGroupsKey]);

  const handleHideGroup = (group: IGroupItemView) => {
    const next = new Set(hiddenGroupIds).add(group.groupId);
    setHiddenGroupIds(next);
    try {
      localStorage.setItem(hiddenGroupsKey, JSON.stringify([...next]));
    } catch {
      /* The row still stays hidden for this session. */
    }
    setSelectedGroupIds((ids) => {
      const remaining = new Set(ids);
      remaining.delete(group.groupId);
      return remaining;
    });
    if (selectedGroupId === group.groupId) {
      setSelectedGroupId(null);
      setShowMemberManage(false);
    }
  };

  const displayGroups = useMemo(() => {
    const filtered = (groupList as IGroupItemView[]).filter((item) => {
      if (hiddenGroupIds.has(item.groupId)) return false;
      if (!searchQuery) {
        return true;
      }
      return groupDisplayName(item).toLowerCase().includes(searchQuery.toLowerCase());
    });
    return sortGroupsWithTopFirst(filtered, topGroupIdSet, entryUnreadGroupIds || undefined);
  }, [groupList, searchQuery, topGroupIdSet, entryUnreadGroupIds, hiddenGroupIds]);

  const topDisplayGroups = useMemo(
    () => displayGroups.filter((item) => isTopGroupId(topGroupIdSet, item.groupId)),
    [displayGroups, topGroupIdSet]
  );
  const normalDisplayGroups = useMemo(
    () => displayGroups.filter((item) => !isTopGroupId(topGroupIdSet, item.groupId)),
    [displayGroups, topGroupIdSet]
  );

  const discussionGroups = useMemo(() => {
    if (!discussionId) {
      return [];
    }

    const orderedTags =
      useDiscussionTabs && discussionTags.includes(0)
        ? [0, ...discussionTags.filter((tag) => tag !== 0)]
        : discussionTags;

    return Array.from(new Set(orderedTags))
      .map((tag) => {
        if (tag === 0) {
          return {
            tag: 0,
            name: t('SharePublic')
          };
        }

        const group = (groupList as IGroupItemView[]).find((item) => item.groupId === tag);

        if (!group) {
          return null;
        }

        return {
          tag,
          name: groupDisplayName(group)
        };
      })
      .filter((item): item is { tag: number; name: string } => item !== null);
  }, [discussionId, discussionTags, groupList, t, useDiscussionTabs]);

  const selectedDiscussionName = useDiscussionTabs
    ? discussionGroups.find((item) => item.tag === selectedDiscussionTag)?.name || ''
    : '';

  const selectedGroupsAreTop =
    selectedGroupIds.size > 0 && Array.from(selectedGroupIds).every((groupId) => isTopGroupId(topGroupIdSet, groupId));
  // Same pattern as delete: only show stick/unpin when every selected
  // group shares the same pin state (all pinned or all unpinned).
  const selectedGroupsTopUniform =
    selectedGroupIds.size > 0 &&
    (selectedGroupsAreTop || Array.from(selectedGroupIds).every((groupId) => !isTopGroupId(topGroupIdSet, groupId)));

  const selectedGroupsCanDelete =
    selectedGroupIds.size > 0 &&
    Array.from(selectedGroupIds).every((groupId) => {
      const group = groupList.find((item) => item.groupId === groupId);
      return !!(group?.isOneOnOneGroup || group?.isGroupLeader);
    });

  // WebChat fills the flex region below ChatRoomHeader. Only pass a pixel
  // budget when the host gave us pixels (WebIDigestApp); for calc()/dvh
  // (vine-web) use 100% so parseInt cannot turn the height into NaNpx.
  const chatHeight = useMemo(() => {
    const trimmed = String(height || '').trim();
    const pxMatch = trimmed.match(/^(\d+(?:\.\d+)?)px$/i);
    if (pxMatch) {
      return `${Math.max(Number(pxMatch[1]) - (useDiscussionTabs ? 100 : 52), 240)}px`;
    }
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber) && trimmed !== '') {
      return `${Math.max(asNumber - (useDiscussionTabs ? 100 : 52), 240)}px`;
    }
    return '100%';
  }, [height, useDiscussionTabs]);

  const handleShareTargetConfirm = useCallback(
    async (targetGroupIds: number[]) => {
      if (targetGroupIds.length === 0 || shareMessages.length === 0 || shareSourceGroupId === -1) {
        setShowShareTargetDialog(false);
        return;
      }

      const messageIds = shareMessages
        .map((msg) => {
          const rawId = msg?.id ?? msg?.messageId;
          const id = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId;
          return typeof id === 'number' && Number.isFinite(id) && id > 0 ? id : null;
        })
        .filter((id): id is number => id != null);

      if (messageIds.length === 0) {
        showAlertDialog(t('Chatroom.ShareFailed', { count: targetGroupIds.length }));
        setShowShareTargetDialog(false);
        setShareMessages([]);
        setShareSourceGroupId(-1);
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const targetGroupId of targetGroupIds) {
        try {
          const newIds = await _dataProvider.batchShareMessages(
            shareSourceGroupId,
            targetGroupId,
            messageIds,
            shareMode
          );
          if (newIds && newIds.length > 0) {
            successCount += newIds.length;
          } else {
            failCount += messageIds.length;
          }
        } catch (error) {
          console.error('[Forward] Error forwarding messages to group', targetGroupId, error);
          failCount += messageIds.length;
        }
      }

      if (failCount === 0) {
        showAlertDialog(t('Chatroom.ShareSuccess', { count: successCount }));
      } else if (successCount === 0) {
        showAlertDialog(t('Chatroom.ShareFailed', { count: failCount }));
      } else {
        showAlertDialog(
          t('Chatroom.SharePartialSuccess', {
            success: successCount,
            fail: failCount
          })
        );
      }

      setShowShareTargetDialog(false);
      setShareMessages([]);
      setShareSourceGroupId(-1);
      setShareMode('individual');
    },
    [shareMessages, shareSourceGroupId, shareMode, t, showAlertDialog]
  );

  const handleGroupToggle = useCallback((groupId: number, checked: boolean) => {
    setSelectedGroupIds((prevSelected) => {
      const next = new Set(prevSelected);
      if (checked) {
        next.add(groupId);
      } else {
        next.delete(groupId);
      }
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedGroupIds(new Set());
    setGroupSelectionMode(false);
  }, []);

  const handleMarkUnread = useCallback(() => {
    if (selectedGroupIds.size === 0) {
      return;
    }
    updatePendingReadGroupIds((prevPending) => {
      const nextPending = new Set(prevPending);
      selectedGroupIds.forEach((groupId) => nextPending.add(groupId));
      return nextPending;
    });
    setSelectedGroupIds(new Set());
    setGroupSelectionMode(false);
  }, [selectedGroupIds, updatePendingReadGroupIds]);

  const persistTopGroupIds = useCallback(
    async (nextTopGroupIds: number[]) => {
      const previous = topGroupIds;
      const normalized = normalizeTopGroupIds(nextTopGroupIds);
      setTopGroupIds(normalized);
      const ok = await _dataProvider.setGroupViewTopList(normalized);
      if (!ok) {
        setTopGroupIds(previous);
        showAlertDialog(t('GroupScreen.FailedToUpdateTopGroups'));
      }
    },
    [topGroupIds, showAlertDialog, t]
  );

  const handleToggleTopGroup = useCallback(
    (group: IGroupItemView, isCurrentlyTop: boolean) => {
      void persistTopGroupIds(toggleTopGroupIds(topGroupIds, group.groupId, isCurrentlyTop));
    },
    [persistTopGroupIds, topGroupIds]
  );

  const handleToggleTopSelected = useCallback(() => {
    if (selectedGroupIds.size === 0) {
      return;
    }
    const selectedIds = Array.from(selectedGroupIds);
    const allAreTop = selectedIds.every((groupId) => isTopGroupId(topGroupIdSet, groupId));
    let next = normalizeTopGroupIds(topGroupIds);
    if (allAreTop) {
      const remove = new Set(selectedIds.map((id) => Number(id)));
      next = next.filter((id) => !remove.has(id));
    } else {
      const seen = new Set(next);
      for (const groupId of selectedIds) {
        const id = Number(groupId);
        if (!Number.isFinite(id) || seen.has(id)) {
          continue;
        }
        seen.add(id);
        next.push(id);
      }
    }
    void persistTopGroupIds(next);
    setSelectedGroupIds(new Set());
    setGroupSelectionMode(false);
  }, [selectedGroupIds, topGroupIdSet, topGroupIds, persistTopGroupIds]);

  const handleDeleteClick = useCallback(() => {
    if (!selectedGroupsCanDelete) {
      return;
    }
    setShowDeleteConfirm(true);
  }, [selectedGroupsCanDelete]);

  const handleConfirmDelete = useCallback(async () => {
    const groupIdsToDelete = Array.from(selectedGroupIds);
    setIsDeleting(true);
    try {
      const result = await _dataProvider.deleteGroups(groupIdsToDelete);
      setShowDeleteConfirm(false);
      const newGroupList = await reloadGroups().catch(() => groupList);
      const success: number[] = result?.success || [];
      const failed: { groupId: number; error: string }[] = result?.failed || [];

      if (selectedGroupId !== null && success.includes(selectedGroupId)) {
        setSelectedGroupId(null);
      }

      if (failed.length > 0) {
        const notLeaderErrors = failed.filter((f) => f.error === 'Not a leader');
        let message = '';
        const hasSuccess = success.length > 0;

        if (notLeaderErrors.length > 0) {
          const removedGroupIds: number[] = [];
          const stillInListGroupIds: number[] = [];

          notLeaderErrors.forEach((f) => {
            const exists = newGroupList.some((g) => g.groupId === f.groupId);
            if (exists) {
              stillInListGroupIds.push(f.groupId);
            } else {
              removedGroupIds.push(f.groupId);
            }
          });

          const messages: string[] = [];
          if (removedGroupIds.length > 0) {
            messages.push(
              hasSuccess
                ? t('GroupScreen.MultipleDeleteCompletedButSomeFailedUserRemoved')
                : t('GroupScreen.UserRemovedFromGroup')
            );
          }
          if (stillInListGroupIds.length > 0) {
            messages.push(
              hasSuccess
                ? t('GroupScreen.MultipleDeleteCompletedButSomeFailedCheckPermission')
                : t('GroupScreen.GroupDeleteFailedCheckPermission')
            );
          }
          if (messages.length > 0) {
            message = messages.join('；');
          }
        }

        if (failed.length > 0 && !message) {
          message = hasSuccess ? t('GroupScreen.FailedToDeleteSomeGroups') : t('GroupScreen.FailedToDeleteGroups');
        }

        setDeleteErrorInfo(message || t('GroupScreen.FailedToDeleteGroups'));
        setShowDeleteFailedDialog(true);
      }

      setSelectedGroupIds(new Set());
    } catch {
      setDeleteErrorInfo(t('GroupScreen.FailedToDeleteGroups'));
      setShowDeleteConfirm(false);
      setShowDeleteFailedDialog(true);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedGroupIds, reloadGroups, groupList, selectedGroupId, t]);

  const handleOpenGroup = useCallback(
    (group: IGroupItemView) => {
      if (isBatchSelectMode) {
        setIsBatchSelectMode(false);
        setSelectedMessageCount(0);
        setHasOthersMessages(false);
        webChatBatchHandlersRef.current?.onExit?.();
      }

      setShowMemberManage(false);
      setSelectedGroupId(group.groupId);
      setVisitedGroups((prev) => [group.groupId, ...prev]);
      updatePendingReadGroupIds((prevPending) => {
        if (!prevPending.has(group.groupId)) {
          return prevPending;
        }
        const nextPending = new Set(prevPending);
        nextPending.delete(group.groupId);
        return nextPending;
      });
    },
    [isBatchSelectMode, updatePendingReadGroupIds]
  );

  const handleJoinMeeting = useCallback(() => {
    if (selectedGroupId === null) {
      return;
    }
    const hostJoin = getIdigestChatHostConfig().onJoinMeeting;
    if (hostJoin) {
      void hostJoin(selectedGroupId);
      return;
    }
    void openMeeting({ groupId: selectedGroupId });
  }, [selectedGroupId]);

  const handleAddNewGroup = useCallback(() => {
    setShowAddNewGroup(false);
    void reloadGroups();
  }, [reloadGroups]);

  const normalActions =
    selectedGroupId !== null ? (
      <div className='header-actions'>
        <div className='button-with-label'>
          <DefaultButton
            className='circular-button'
            title={t('JoinGroupCall')}
            ariaLabel={t('JoinGroupCall')}
            styles={circularButtonStyles}
            onClick={handleJoinMeeting}
            onMouseEnter={() => setIsConferenceHovered(true)}
            onMouseLeave={() => setIsConferenceHovered(false)}
          >
            <img
              src={isConferenceHovered ? ConferenceHoverIcon : ConferenceIcon}
              alt='Conference'
              className='normal'
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </DefaultButton>
          <span>{t('JoinGroupCall')}</span>
        </div>
        {yesGroupManagement && (
          <div className='button-with-label'>
            <DefaultButton
              className='circular-button'
              title={t('ManageMembers')}
              ariaLabel={t('ManageMembers')}
              styles={circularButtonStyles}
              onClick={() => setShowMemberManage(true)}
              onMouseEnter={() => setIsManageHovered(true)}
              onMouseLeave={() => setIsManageHovered(false)}
            >
              <img
                src={isManageHovered ? ManageMembersHoverIcon : ManageMembersIcon}
                alt='Manage'
                className='normal'
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </DefaultButton>
            <span>{t('ManageMembers')}</span>
          </div>
        )}
      </div>
    ) : null;

  const listStatusMessage = loading ? t('loading') : txt || t('NoGroup');

  const discussionTabs = useDiscussionTabs ? (
    <div className='idigest-chat-discussion-tabs' role='tablist' aria-label={t('GroupChat')}>
      {discussionGroups.map((item) => (
        <button
          key={item.tag}
          type='button'
          role='tab'
          aria-selected={selectedDiscussionTag === item.tag}
          className={`idigest-chat-discussion-tab${
            selectedDiscussionTag === item.tag ? ' idigest-chat-discussion-tab--active' : ''
          }`}
          onClick={() => setSelectedDiscussionTag(item.tag)}
        >
          {item.name}
        </button>
      ))}
    </div>
  ) : null;

  const listPane = (
    <>
      <GroupListHeader
        onNewGroup={() => setShowAddNewGroup(true)}
        onMassMessage={() => setShowMassMessage(true)}
        onShowContacts={() => setShowContacts(true)}
        t={t}
        selectedCount={selectedGroupIds.size}
        selectionMode={groupSelectionMode}
        onClearSelection={handleClearSelection}
        onDelete={selectedGroupsCanDelete ? handleDeleteClick : undefined}
        onMarkUnread={handleMarkUnread}
        selectionAreTop={selectedGroupsAreTop}
        onToggleTop={selectedGroupsTopUniform ? handleToggleTopSelected : undefined}
      />
      <div className='searchbox-wrapper group-list-pane-pad'>
        {groupList.length > 0 && (
          <SearchBoxChrome>
            <SearchBox
              styles={searchBoxStyles}
              value={searchQuery}
              placeholder={t('Search')}
              onEscape={() => setSearchQuery('')}
              onChange={(_, value) => setSearchQuery(value || '')}
              onClear={() => setSearchQuery('')}
            />
          </SearchBoxChrome>
        )}
      </div>
      <div className='fellowshipGroup group-list-scroll'>
        <div className='grouplist-wrapper group-list-pane-pad'>
          {discussionId ? (
            <>
              {discussionGroups.map((item) => (
                <button
                  key={item.tag}
                  onClick={() => setSelectedDiscussionTag(item.tag)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    textAlign: 'left',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: selectedDiscussionTag === item.tag ? 600 : 400
                  }}
                >
                  {item.name}
                </button>
              ))}
            </>
          ) : (
            <>
              {groupList.length === 0 && <p>{listStatusMessage}</p>}

              {topDisplayGroups.length > 0 && (
                <div className='group-list-top-section'>
                  {topDisplayGroups.map((item, index) => (
                    <GroupListItem
                      key={item.groupId}
                      group={item}
                      index={index}
                      isActive={selectedGroupId === item.groupId}
                      isSelected={selectedGroupIds.has(item.groupId)}
                      canSelect={true}
                      selectionMode={groupSelectionMode}
                      onSelect={(group) => {
                        setGroupSelectionMode(true);
                        handleGroupToggle(group.groupId, true);
                      }}
                      onManage={(group) => {
                        handleOpenGroup(group);
                        setShowMemberManage(true);
                      }}
                      onHide={handleHideGroup}
                      onLeave={setLeaveGroup}
                      onMarkUnread={(group) => updatePendingReadGroupIds((ids) => new Set(ids).add(group.groupId))}
                      isInVisitedGroups={visitedGroups.indexOf(item.groupId) !== -1}
                      isPendingRead={pendingReadGroupIds.has(item.groupId)}
                      isTop={true}
                      groupDisplayName={groupDisplayName}
                      onToggle={handleGroupToggle}
                      onClick={handleOpenGroup}
                      onToggleTop={handleToggleTopGroup}
                      userHideMessages={userHideMessages}
                      t={t}
                      theme={theme}
                    />
                  ))}
                </div>
              )}

              {normalDisplayGroups.map((item, index) => (
                <GroupListItem
                  key={item.groupId}
                  group={item}
                  index={index + topDisplayGroups.length}
                  isActive={selectedGroupId === item.groupId}
                  isSelected={selectedGroupIds.has(item.groupId)}
                  canSelect={true}
                  selectionMode={groupSelectionMode}
                  onSelect={(group) => {
                    setGroupSelectionMode(true);
                    handleGroupToggle(group.groupId, true);
                  }}
                  onManage={(group) => {
                    handleOpenGroup(group);
                    setShowMemberManage(true);
                  }}
                  onHide={handleHideGroup}
                  onLeave={setLeaveGroup}
                  onMarkUnread={(group) => updatePendingReadGroupIds((ids) => new Set(ids).add(group.groupId))}
                  isInVisitedGroups={visitedGroups.indexOf(item.groupId) !== -1}
                  isPendingRead={pendingReadGroupIds.has(item.groupId)}
                  isTop={false}
                  groupDisplayName={groupDisplayName}
                  onToggle={handleGroupToggle}
                  onClick={handleOpenGroup}
                  onToggleTop={handleToggleTopGroup}
                  userHideMessages={userHideMessages}
                  t={t}
                  theme={theme}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );

  const chatRoomHeader = (
    <ChatRoomHeader
      title={useDiscussionTabs ? selectedDiscussionName : selectedGroupName}
      theme={theme}
      showBack={!useSplitLayout && !useDiscussionTabs}
      autoDeleteNotice={!useDiscussionTabs ? selectedGroupAutoDeleteNotice : undefined}
      onBack={() => {
        if (discussionId) {
          setSelectedDiscussionTag(null);
        } else {
          setSelectedGroupId(null);
        }
      }}
      isBatchSelectMode={isBatchSelectMode}
      selectedCount={selectedMessageCount}
      hasOthersMessages={hasOthersMessages}
      activeFilter={activeFilter}
      currentUserIsLeader={currentUserIsLeader}
      normalActions={normalActions}
      onExitBatchSelect={() => {
        setIsBatchSelectMode(false);
        setSelectedMessageCount(0);
        setHasOthersMessages(false);
        webChatBatchHandlersRef.current?.onExit?.();
      }}
      onBatchGroupCollection={() => {
        webChatBatchHandlersRef.current?.onGroupCollection?.();
      }}
      onBatchMyCollection={() => {
        webChatBatchHandlersRef.current?.onMyCollection?.();
      }}
      onBatchDelete={() => {
        webChatBatchHandlersRef.current?.onDelete?.();
      }}
      onBatchRemoveGroupCollection={() => {
        webChatBatchHandlersRef.current?.onRemoveGroupCollection?.();
      }}
      onBatchRemoveMyCollection={() => {
        webChatBatchHandlersRef.current?.onRemoveMyCollection?.();
      }}
    />
  );

  const detailPane =
    selectedGroupId !== null || discussionId ? (
      <>
        {(!useDiscussionTabs || isBatchSelectMode) && chatRoomHeader}
        <div
          className='idigest-chat-detail-body'
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: tokens.bg,
            position: 'relative'
          }}
        >
          {showMemberManage && selectedGroup && (
            <Suspense fallback={null}>
              <GroupMemberTool
                groupId={selectedGroup.groupId}
                isGroupLeader={!!selectedGroup.isGroupLeader}
                show={showMemberManage}
                onClose={() => setShowMemberManage(false)}
                launchOneOnOneWithFriend={launchOneOnOneWithFriend}
                contactList={contactList}
                onDisappearingMessagesUpdated={() => {
                  void reloadGroups();
                }}
              />
            </Suspense>
          )}
          <WebChat
            groupId={discussionId ?? selectedGroupId}
            discussionShareInGroup={discussionId && selectedDiscussionTag !== null ? selectedDiscussionTag : undefined}
            onDiscussionTagsLoaded={
              discussionId
                ? (tags) => {
                    setDiscussionTags(tags);

                    setSelectedDiscussionTag((currentTag) => {
                      if (useDiscussionTabs) {
                        const firstAvailableTag = tags.includes(0) ? 0 : (tags[0] ?? null);

                        return currentTag !== null && tags.includes(currentTag) ? currentTag : firstAvailableTag;
                      }

                      return currentTag !== null && tags.includes(currentTag) ? currentTag : null;
                    });
                  }
                : undefined
            }

            height={chatHeight}
            theme={theme}
            disappearingMessages={selectedGroupDisappearingMessages}
            onGroupInfoUpdated={handleGroupInfoUpdated}
            launchOneOnOneWithFriend={launchOneOnOneWithFriend}
            onBatchSelectModeChange={(isBatchMode, selectedCount, hasOthers) => {
              setIsBatchSelectMode(isBatchMode);
              setSelectedMessageCount(selectedCount);
              setHasOthersMessages(!!hasOthers);
            }}
            onShareTargetSelectRequest={(method, selectedMessages, sourceGroupId) => {
              setShareMessages(selectedMessages || []);
              setShareSourceGroupId(sourceGroupId ?? selectedGroupId ?? -1);
              setShareMode(method === 'package' ? 'merged' : 'individual');
              setShowShareTargetDialog(true);
            }}
            onFilterStateChange={setActiveFilter}
            batchHandlersRef={webChatBatchHandlersRef}
          />
        </div>
      </>
    ) : (
      <>
        <ChatRoomHeader title={t('GroupChat')} theme={theme} showBack={false} />
        <div
          className='idigest-chat-detail-empty'
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: tokens.muted,
            backgroundColor: tokens.bg
          }}
        />
      </>
    );

  const content = useDiscussionTabs ? (
    <div className={rootClassName} style={rootStyle}>
      {discussionTabs}
      <div className='idigest-chat-discussion-detail'>{detailPane}</div>
    </div>
  ) : useSplitLayout ? (
    <div className={rootClassName} style={rootStyle}>
      <div className='idigest-chat-split group-mgmt-container'>
        <div className='idigest-chat-split-list left-panel'>{listPane}</div>
        <div className='idigest-chat-split-detail middle-panel'>{detailPane}</div>
      </div>
    </div>
  ) : (
    <div className={rootClassName} style={rootStyle}>
      {discussionId ? (
        <>
          {selectedDiscussionTag === null && listPane}

          <div
            style={{
              display: selectedDiscussionTag === null ? 'none' : 'flex',
              flex: 1,
              minHeight: 0,
              flexDirection: 'column'
            }}
          >
            {detailPane}
          </div>
        </>
      ) : selectedGroupId === null ? (
        listPane
      ) : (
        detailPane
      )}
    </div>
  );

  return (
    <IdigestChatThemeProvider theme={theme}>
      {content}
      {showMassMessage && (
        <Suspense fallback={null}>
          <AddMassMessagesWindow onClose={() => setShowMassMessage(false)} />
        </Suspense>
      )}
      {showAddNewGroup && (
        <Suspense fallback={null}>
          <AddNewGroupWindow
            onClose={() => setShowAddNewGroup(false)}
            onAddGroup={handleAddNewGroup}
            users={contactList.filter((user) => user.loginId !== currentUser.uniqueId)}
          />
        </Suspense>
      )}
      <PromptContactList
        isOpen={showContacts}
        onDismiss={() => setShowContacts(false)}
        contactList={contactList}
        currentUser={currentUser}
        launchOneOnOneWithFriend={launchOneOnOneWithFriend}
      />
      {showShareTargetDialog && (
        <PromptPickShareTarget
          onConfirm={handleShareTargetConfirm}
          onDismiss={() => {
            setShowShareTargetDialog(false);
            setShareMessages([]);
            setShareSourceGroupId(-1);
          }}
        />
      )}
      {showInitialGreeting && (
        <OneOnOneGreetingDialog
          displayName={initialOneOnOneDisplayName}
          onDismiss={() => {
            setShowInitialGreeting(false);
            onInitialOneOnOneDismiss?.();
          }}
          onSubmit={handleInitialGreetingSubmit}
        />
      )}
      <ConfirmDialog
        show={!!leaveGroup}
        title={t('Exit')}
        confirmText={t('Confirm')}
        cancelText={t('Cancel')}
        content={t('GroupScreen.LeaveConfirm', { name: leaveGroup ? groupDisplayName(leaveGroup) : '' })}
        isLoading={isLeavingGroup}
        onCancel={() => {
          if (!isLeavingGroup) setLeaveGroup(null);
        }}
        onConfirm={async () => {
          if (!leaveGroup || isLeavingGroup) return;
          setIsLeavingGroup(true);
          try {
            const success = await _dataProvider.leaveGroup(leaveGroup.groupId);
            if (success) {
              if (selectedGroupId === leaveGroup.groupId) setSelectedGroupId(null);
              handleGroupToggle(leaveGroup.groupId, false);
              await reloadGroups();
            } else {
              showAlertDialog(t('GroupScreen.LeaveFailed'));
            }
            setLeaveGroup(null);
          } finally {
            setIsLeavingGroup(false);
          }
        }}
      />
      <ConfirmDialog
        show={showDeleteConfirm}
        title={t('GroupScreen.DeleteGroupsConfirmTitle')}
        content={t('GroupScreen.DeleteGroupsConfirmMessage').replace('{{count}}', selectedGroupIds.size.toString())}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmText={t('Confirm')}
        cancelText={t('Cancel')}
        isLoading={isDeleting}
      />
      <ConfirmDialog
        show={showDeleteFailedDialog}
        title={t('GroupScreen.Warning')}
        content={deleteErrorInfo || t('GroupScreen.FailedToDeleteGroups')}
        onClose={() => setShowDeleteFailedDialog(false)}
        showConfirm={false}
        showCancel={false}
        closeText={t('Close')}
      />
      <ConfirmDialog
        show={alertDialog.show}
        content={alertDialog.content}
        onClose={() => setAlertDialog((prev) => ({ ...prev, show: false }))}
        showConfirm={false}
        showCancel={false}
        closeText={t('OK')}
      />
    </IdigestChatThemeProvider>
  );
}
